import { Add } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { CalculateTour } from '@/domain/impact-calculate-tour/entities/calculate-tour.entity';
import { selectImpactCalculateTourForCurrentProject } from '@/domain/impact-calculate-tour/impact-calculate-tour.selectors';
import { deleteTourSequence } from '@/domain/project-tour-ontour/actions/delete-tour-sequence.action';
import { TourSequence } from '@/domain/project-tour-ontour/entities/project-tour-ontour-sequence.entity';
import { selectProjectTourOnTourSequences } from '@/domain/project-tour-ontour/project-tour-ontour.selectors';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { useDialogConfirm } from '@/ui/components/confirm-dialog/useDialogConfirm';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';

import { Co2OnTourDetailsSequencesItem } from './Co2OnTourDetailsSequencesItem';
import { ModalTourSequenceAdd } from './tour-sequence/ModalTourSequenceAdd';
import { ModalTourSequenceEdit } from './tour-sequence/ModalTourSequenceEdit';

export function Co2OnTourDetailsSequences({
  onClickOpenOnTourSequence,
}: {
  onClickOpenOnTourSequence: (sequenceId: number) => void;
}) {
  const dispatch = useAppDispatch();
  const dialogConfirm = useDialogConfirm();

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSequence, setEditingSequence] = useState<TourSequence | null>(
    null,
  );

  const [addModalOpen, setAddModalOpen] = useState(false);

  const sequences = useAppSelector(selectProjectTourOnTourSequences);

  const calculateTour = useAppSelector(
    selectImpactCalculateTourForCurrentProject,
  );

  const onClickAddSequence = () => {
    setAddModalOpen(true);
  };

  const onCloseAddModal = () => {
    setAddModalOpen(false);
  };

  const onClickOpenSequence = (sequence: TourSequence) => {
    onClickOpenOnTourSequence(sequence.id);
  };

  const onClickEditSequence = (sequence: TourSequence) => {
    setEditingSequence(sequence);
    setEditModalOpen(true);
  };

  const onCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingSequence(null);
  };

  const onClickDeleteSequence = async (sequence: TourSequence) => {
    const response = await dialogConfirm({
      title: translate('DeleteTourSequence.Message.confirmation'),
    });
    if (response !== 'confirmed') {
      return;
    }
    dispatch(deleteTourSequence(sequence.id));
  };

  return (
    <>
      <ModalTourSequenceAdd open={addModalOpen} onClose={onCloseAddModal} />
      <ModalTourSequenceEdit
        open={editModalOpen}
        onClose={onCloseEditModal}
        sequence={editingSequence}
      />
      <Stack sx={{ gap: 2 }}>
        {sequences.length === 0 && <TourSequenceListEmpty />}
        {sequences.map((sequence) => (
          <Box key={sequence.id}>
            <SequenceLine
              sequence={sequence}
              calculateTour={calculateTour}
              onClickOpen={() => onClickOpenSequence(sequence)}
              onClickEdit={() => onClickEditSequence(sequence)}
              onClickDelete={() => onClickDeleteSequence(sequence)}
            />
          </Box>
        ))}
        <Stack sx={{ my: 2, alignItems: 'center' }}>
          <FSPrimaryButton
            color="info"
            startIcon={<Add />}
            onClick={onClickAddSequence}
          >
            {translate('ProjectsTour.OnTour.add-sequence-button')}
          </FSPrimaryButton>
        </Stack>
      </Stack>
    </>
  );
}

function SequenceLine({
  sequence,
  calculateTour,
  onClickOpen,
  onClickEdit,
  onClickDelete,
}: {
  sequence: TourSequence;
  calculateTour: CalculateTour | null;
  onClickOpen: () => void;
  onClickEdit: () => void;
  onClickDelete: () => void;
}) {
  const tourEventCount =
    calculateTour?.stats?.ontour?.bySequence[sequence.id]?.eventCount || 0;

  const co2PerEvent =
    calculateTour?.stats?.ontour?.bySequence[sequence.id]?.co2AveragePerEvent ||
    0;

  const sequenceScoreCo2 =
    calculateTour?.co2?.ontour.bySequence[sequence.id]?.score || 0;

  return (
    <Co2OnTourDetailsSequencesItem
      sequence={sequence}
      eventCount={tourEventCount}
      co2={sequenceScoreCo2}
      co2PerEvent={co2PerEvent}
      onClickOpen={onClickOpen}
      onClickEdit={onClickEdit}
      onClickDelete={onClickDelete}
    />
  );
}

function TourSequenceListEmpty() {
  return (
    <Box sx={{ py: 4, px: 4 }}>
      <Typography
        sx={{
          textAlign: 'center',
        }}
      >
        {translate('ProjectsTour.on-tour-description-empty-sequences')}
      </Typography>
    </Box>
  );
}
